import { fixFor, serviceCapability, type NodeServiceDTO, type ServiceHost, type ServiceName, type ServiceState } from "@roomate/core";

export type ChipTone = "live" | "signal" | "neutral" | "danger";

/** State chip wording (machine-connected-services §8). `state` arrives as a plain string from the hub; anything unrecognised reads as "Could not check". */
const STATE: Record<string, { label: string; tone: ChipTone }> = {
  connected: { label: "Ready", tone: "live" },
  ready: { label: "Ready", tone: "live" },
  not_installed: { label: "CLI missing", tone: "neutral" },
  not_logged_in: { label: "Not logged in", tone: "signal" },
  no_access: { label: "No access", tone: "danger" },
  unknown: { label: "Could not check", tone: "neutral" },
};

export function serviceChip(state: string): { label: string; tone: ChipTone } {
  return STATE[state] ?? STATE.unknown!;
}

/** The catalog's display name for a service, falling back to the raw name. */
export function serviceTitle(name: string): string {
  return serviceCapability(name)?.label ?? name;
}

/**
 * What to show for a service that is not ready: the catalog's guidance sentence and, when it names one, the command to run
 * (the first backtick-quoted span of that sentence). Both come from core `fixFor` by `(name, state, scope)`, never from
 * anything the node reported. `command` is null for "could not check", which is advice, not something to paste.
 */
export function serviceFix(service: Pick<NodeServiceDTO, "name" | "scope" | "state">): { text: string; command: string | null } | null {
  const name = service.name as ServiceName;
  if (!serviceCapability(name)) return null;
  const state = (service.state === "no_access" ? "unknown" : service.state) as ServiceState;
  const text = fixFor(name, state, service.scope as ServiceHost);
  if (!text) return null;
  return { text, command: /`([^`]+)`/.exec(text)?.[1] ?? null };
}
